<template>
  <div>
    <GeojsonLayer
      :sourceId="sourceId"
      :source="realEstateSource"
      :layer="layerRealEstateStyle"
      @layerClicked="handleClick"
      @layerMouseEnter="setCursor('pointer')"
      @layerMouseLeave="handleMouseLeave"
      @layerMouseMove="updateHoverId"
      @layerRightClicked="handleRightClick"
    />
    <GeojsonLayer
      :sourceId="sourceId"
      :source="realEstateSource"
      :layer="layerRealEstateLinesStyle"
    />
    <GeojsonLayer
      :sourceId="sourceId"
      :source="realEstateSource"
      :layer="layerRealEstateLinesDashedStyle"
    />
    <div v-show="false">
      <MapPopup ref="popup" :rows="[createSmallTitle(popupTitle)]" />
    </div>
    <div v-show="false">
      <MapContextMenu
        v-if="hasPoiCreatorAccess"
        ref="rightClickButtons"
        :buttons="contextMenuButtons"
        @clickCreatePOI="handleClickOnCreatePOIButton"
      />
    </div>
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import { useGeneralStore } from '@/pinia'
import { mapState as mapStatePinia } from 'pinia'
import GeojsonLayer from '@/components/map/mapbox-layers/GeojsonLayer.vue'
import MapContextMenu from '@/components/map/popups/MapContextMenu.vue'
import MapPopup from '@/components/map/popups/MapPopup.vue'
import { CHANGE_REAL_ESTATE, CHANGE_POI, RESET_CURRENT_LEVEL } from '@/store/action-types'
import { RESET_CURRENT_BUILDING, SET_CLICKED_ITEM_TYPE } from '@/store/mutation-types'
import { MAPVIEW_PREFIX } from '@/store/module-prefixes'
import { createSmallTitle } from '@/components/Builder'

export default {
  name: 'LayerRealEstate',
  components: {
    GeojsonLayer,
    MapContextMenu,
    MapPopup
  },
  props: {
    layerIdRealEstate: {
      type: String,
      required: true
    },
    source: {
      validator: (prop) => typeof prop === 'object' || prop == null,
      default: null
    }
  },
  setup() {
    const {
      setCurrentOverlayModalPinia,
      setRightClickCoordinate,
      setShowOverlayModalPinia,
      setSelectedLine,
      setSelectedZone
    } = useGeneralStore()
    return {
      setCurrentOverlayModalPinia,
      setRightClickCoordinate,
      setShowOverlayModalPinia,
      setSelectedLine,
      setSelectedZone,
      createSmallTitle
    }
  },
  data() {
    return {
      layerIdRealEstateLines: 'layer-real-estate-lines',
      layerIdRealEstateLinesDashed: 'layer-real-estate-lines-dashed',
      sourceId: 'source-real-estate',
      hoveredRealEstateId: null,
      layerDashLinesId: 'realEstateLayerDashLines',
      layerLinesId: 'realEstateLayerLines',
      popupTitle: '',
      popup: new mapboxgl.Popup({ closeButton: false, offset: 5 }),
      rightClickContextMenu: new mapboxgl.Popup({
        anchor: 'top-left',
        className: 'layer-realestate-map-context-menu',
        closeButton: false
      }),
      rightClickCoordinate: []
    }
  },
  computed: {
    ...mapGetters({
      isDrawPointActive: 'isDrawPointActive',
      isMapviewShowing: 'isMapviewShowing',
      isMeasurementActive: 'isMeasurementActive',
      hasPoiCreatorAccess: 'hasPoiCreatorAccess'
    }),
    ...mapGetters(MAPVIEW_PREFIX, {
      selectedRealEstateId: 'selectedRealEstateId'
    }),
    ...mapState({
      clickedItemType: (state) => state.clickedItemType,
      currentZoom: (state) => state.currentZoom
    }),
    ...mapState(MAPVIEW_PREFIX, {
      filteredMapLayers: (state) => state.filteredLayers,
      selectedRealEstate: (state) => state.selectedRealEstate
    }),
    ...mapStatePinia(useGeneralStore, [
      'currentOverlayModalPinia',
      'showOverlayModalPinia',
      'selectedAttachment',
      'selectedTicket'
    ]),
    contextMenuButtons() {
      const buttons = []
      buttons.push({
        title: this.$t('general__text__create-new-asset'),
        emit: 'clickCreatePOI',
        icon: 'IconPlusMedium'
      })
      return buttons
    },
    realEstateSource() {
      const newSource = {
        type: 'geojson',
        data: {
          id: this.sourceId,
          type: 'FeatureCollection',
          features: []
        }
      }
      if (this.source) {
        newSource.data = this.source.data
      }
      return newSource
    },
    layerRealEstateLinesDashedStyle() {
      return {
        id: this.layerIdRealEstateLinesDashed,
        source: this.sourceId,
        type: 'line',
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
          visibility: this.lineLayerVisible ? 'visible' : 'none'
        },
        paint: {
          'line-color': '#fff',
          'line-dasharray': [5, 2.5],
          'line-opacity': [
            'case',
            [
              'all',
              this.clickedItemType === 'realEstate' ||
                this.clickedItemType === 'poi' ||
                this.clickedItemType === 'line' ||
                this.clickedItemType === 'zone',
              ['boolean', ['==', this.selectedRealEstateId, ['get', 'id']], false]
            ],
            1,
            0
          ],
          'line-width': 5
        }
      }
    },
    layerRealEstateLinesStyle() {
      return {
        id: this.layerIdRealEstateLines,
        source: this.sourceId,
        type: 'line',
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
          visibility: this.lineLayerVisible ? 'visible' : 'none'
        },
        paint: {
          'line-color': '#fff',
          'line-opacity': [
            'case',
            [
              'all',
              this.clickedItemType === 'realEstate',
              ['boolean', ['==', this.selectedRealEstateId, ['get', 'id']], false]
            ],
            0,
            1
          ],
          'line-width': 2
        }
      }
    },
    layerRealEstateStyle() {
      return {
        id: this.layerIdRealEstate,
        source: this.sourceId,
        type: 'fill',
        paint: {
          'fill-color': [
            'case',
            ['boolean', ['==', this.selectedAttachmentEntityId, ['get', 'id']], false],
            '#fadb14',
            ['boolean', ['==', this.selectedTicketEntityId, ['get', 'id']], false],
            '#fadb14',
            [
              'all',
              this.clickedItemType === 'realEstate' ||
                this.clickedItemType === 'poi' ||
                this.clickedItemType === 'line' ||
                this.clickedItemType === 'zone',
              ['boolean', ['==', this.selectedRealEstateId, ['get', 'id']], false]
            ],
            '#fff',
            ['boolean', ['==', this.hoveredRealEstateId, ['get', 'id']], false],
            '#80b5b4',
            '#b4d6d6'
          ],
          'fill-opacity': [
            'case',
            [
              'all',
              this.clickedItemType === 'realEstate',
              ['boolean', ['==', this.selectedRealEstateId, ['get', 'id']], false]
            ],
            this.opacity,
            ['boolean', ['==', this.selectedAttachmentEntityId, ['get', 'id']], false],
            0.7,
            ['boolean', ['==', this.selectedTicketEntityId, ['get', 'id']], false],
            0.7,
            ['boolean', ['==', this.hoveredRealEstateId, ['get', 'id']], false],
            this.opacity,
            this.opacity
          ]
        },
        layout: {
          visibility: this.visibility ? 'visible' : 'none'
        }
      }
    },
    opacity() {
      return this.isMapviewShowing ? 0.3 : 0
    },
    selectedAttachmentEntityId() {
      return this.selectedAttachment?.entityId ? this.selectedAttachment.entityId : ''
    },
    selectedTicketEntityId() {
      return this.selectedTicket?.entityConnection?.length &&
        this.selectedTicket?.entityConnection[0].id
        ? this.selectedTicket.entityConnection[0].id
        : ''
    },
    lineLayerVisible() {
      return this.currentZoom > 12.5 && this.visibility
    },
    visibility() {
      const realestateOption = this.filteredMapLayers.find((el) => el.id === 'realestates')
      return this.isMapviewShowing && realestateOption && realestateOption.visible
    }
  },
  watch: {
    hoveredRealEstateId(newId, oldId) {
      if (newId) this.setHoverFeatureState(newId, true)
      if (oldId) {
        this.setHoverFeatureState(oldId, false)
        return
      }
      this.source.data.features.forEach((el) => {
        if (el.id !== newId) {
          this.setHoverFeatureState(el.id, false)
        }
      })
    },
    selectedRealEstateId(newId, oldId) {
      this.source.data.features.forEach((el) => {
        if (el.id !== newId) this.setSelectedFeatureState(el.id, false)
      })
      if (newId) this.setSelectedFeatureState(newId, true)
      if (oldId && newId !== oldId) {
        this.setSelectedFeatureState(oldId, false)
        return
      }
    },
    showOverlayModalPinia(newVal) {
      if (newVal && this.currentOverlayModalPinia === 'editPoi')
        if (this.rightClickContextMenu) this.rightClickContextMenu.remove()
    }
  },
  methods: {
    ...mapActions({
      dispatchRealEstate: CHANGE_REAL_ESTATE,
      dispatchPoi: CHANGE_POI,
      resetCurrentLevel: RESET_CURRENT_LEVEL
    }),
    ...mapMutations({
      commitClick: SET_CLICKED_ITEM_TYPE
    }),
    ...mapMutations(MAPVIEW_PREFIX, {
      resetCurrentBuilding: RESET_CURRENT_BUILDING
    }),
    async handleClick(event) {
      if (this.isDrawPointActive || this.isMeasurementActive || !this.isFirstLayer(event)) return
      const selectedRealEstate = this.source.data.features.find(
        (realEstate) => realEstate.id === event.features[0].properties.belongsToId
      )
      const realEstateEventId = event.features[0].properties.belongsToId
      if (realEstateEventId !== this.selectedRealEstateId) {
        this.dispatchRealEstate({
          realEstate: selectedRealEstate.properties
        })
        this.commitClick({ type: 'realEstate' })
        return
      }
      this.dispatchPoi({ poi: null })
      this.setSelectedLine(null)
      this.setSelectedZone(null)
      this.resetCurrentLevel()
      this.resetCurrentBuilding()
      this.commitClick({ type: 'realEstate' })
    },
    handleClickOnCreatePOIButton() {
      this.setRightClickCoordinate(this.rightClickCoordinate)
      this.setCurrentOverlayModalPinia('createAssets')
      this.setShowOverlayModalPinia(true)
    },
    handleMouseLeave(event) {
      if (this.isMeasurementActive) return
      this.setCursor('')
      this.updateHoverId(event)
    },
    handleRightClick(event) {
      // TODO: Should we handle lines ans zones here? Check with PO for future proofing
      if (
        !this.hasPoiCreatorAccess ||
        !this.$refs.rightClickButtons ||
        !this.isMapviewShowing ||
        this.isDrawPointActive
      )
        return
      if (
        !this.selectedRealEstateId.length ||
        (this.clickedItemType !== 'realEstate' && this.clickedItemType !== 'poi') ||
        this.hoveredRealEstateId !== this.selectedRealEstateId
      )
        return
      this.rightClickCoordinate = [event.lngLat.lng, event.lngLat.lat]
      this.rightClickContextMenu
        .setLngLat(event.lngLat)
        .setDOMContent(this.$refs.rightClickButtons.$el)
        .addTo(this.$store.map)
    },
    isFirstLayer(event) {
      const renderedFeatures = this.$store.map.queryRenderedFeatures(event.point)
      return renderedFeatures.length && renderedFeatures[0].layer.id === this.layerIdRealEstate
    },
    resetHoverId() {
      this.hoveredRealEstateId = null
      this.popup.remove()
    },
    setCursor(cursorType) {
      if (this.isMeasurementActive) return
      this.$store.map.getCanvas().style.cursor = cursorType
    },
    setHoverFeatureState(featureId, hasState) {
      if (!featureId) return
      this.$store.map.setFeatureState({ source: this.sourceId, id: featureId }, { hover: hasState })
    },
    setSelectedFeatureState(featureId, hasState) {
      if (!featureId) return
      this.$store.map.setFeatureState(
        { source: this.sourceId, id: featureId },
        { selected: hasState }
      )
    },
    showPopup(event) {
      if (!this.$refs.popup) return
      const coordinates = event.lngLat
      this.popup.setLngLat(coordinates).setDOMContent(this.$refs.popup.$el).addTo(this.$store.map)
      this.popup.trackPointer()
    },
    updateHoverId(event) {
      const features = event.features
      if (this.isMeasurementActive || !this.isFirstLayer(event) || !features || !features.length) {
        this.resetHoverId()
        return
      }
      this.setCursor('pointer')
      if (features[0].properties.id === this.hoveredRealEstateId) return
      this.hoveredRealEstateId = features[0].properties.belongsToId
      this.popupTitle = features[0].properties.displayName
      this.showPopup(event)
    }
  }
}
</script>
<style lang="scss">
// Be aware that this css isn't scoped!!!
.layer-realestate-map-context-menu {
  .mapboxgl-popup-tip {
    display: none !important;
  }
}
</style>
